import { useEffect, useMemo, useState } from 'react';

import { cn } from '@client/lib';

import { AddPolicyModal } from '@client/components/AddPolicyModal';
import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import {
  getShellExpandKey,
  useAccountAccordion,
} from '@/features/guided-financial-planning/hooks/use-account-accordion';
import { AccountCard } from './account-card';
import { ExistingLtcPolicyCard } from './existing-ltc-policy-card';

interface AccountAccordionListProps {
  className?: string;
  onAllCompleteChange?: (allComplete: boolean) => void;
  disabled?: boolean;
}

export function AccountAccordionList({
  className,
  onAllCompleteChange,
  disabled,
}: AccountAccordionListProps) {
  const { accountShells, allComplete, expandedShellKey, handleToggle, handleSaveComplete } =
    useAccountAccordion();
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();
  const [existingLtcDrawerOpen, setExistingLtcDrawerOpen] = useState(false);

  const existingLtcShell = useMemo(
    () => accountShells.find(s => s.isExistingLtcPolicy),
    [accountShells],
  );
  const policyFundingSourceIdForDrawer = existingLtcShell?.policyFundingSourceId ?? undefined;

  // Build allocation lookup from snapshot for completed sessions
  const allocationsBySourceId = useMemo(() => {
    if (!isSessionComplete) return {};
    const allocations = session?.stepData?.planComparisonSnapshot?.fundingAllocations ?? [];
    return allocations.reduce(
      (acc, allocation) => {
        acc[allocation.selfFundingSourceId] = allocation.amount;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [isSessionComplete, session?.stepData?.planComparisonSnapshot?.fundingAllocations]);

  useEffect(() => {
    onAllCompleteChange?.(allComplete);
  }, [allComplete, onAllCompleteChange]);

  return (
    <>
      <div className={cn('space-y-4', className)}>
        {accountShells.map(shell =>
          shell.isExistingLtcPolicy ? (
            <ExistingLtcPolicyCard
              key="existing-ltc-policy"
              label={shell.label}
              hasPolicy={Boolean(shell.policyFundingSourceId)}
              policyFaceAmount={shell.policyMaximumBenefitAmount ?? undefined}
              policyTypeName={shell.policyTypeName ?? undefined}
              disabled={isSessionComplete ? false : disabled}
              onClick={() => setExistingLtcDrawerOpen(true)}
            />
          ) : (
            <AccountCard
              key={getShellExpandKey(shell)}
              assetTypeId={shell.assetTypeId}
              label={shell.label}
              isExpanded={expandedShellKey === getShellExpandKey(shell)}
              onToggle={() => handleToggle(getShellExpandKey(shell))}
              fundingSourceId={shell.fundingSourceId}
              existingSource={shell.selfFundingSource}
              onSaveComplete={() => handleSaveComplete(getShellExpandKey(shell))}
              disabled={disabled}
              allocationAmount={
                shell.fundingSourceId ? allocationsBySourceId[shell.fundingSourceId] : undefined
              }
            />
          ),
        )}
      </div>
      <AddPolicyModal
        open={existingLtcDrawerOpen}
        onClose={() => setExistingLtcDrawerOpen(false)}
        fundingSourceId={policyFundingSourceIdForDrawer}
        readOnly={isSessionComplete}
      />
    </>
  );
}
